import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CustomDialog from 'components/CustomDialog';
import GoogleSignInButton from 'components/GoogleSignInButton';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { login, logout } from 'store/slices/auth';
import { unsetPathBeforeLoginRequest } from 'store/slices/sessionStatus';
import Grid from '@mui/material/Unstable_Grid2';
import { useAppSelector } from 'hooks/useAppSelector';
import { Button, Link, TextField, Typography } from '@mui/material';
import ErrorMessage from '../ErrorMessage';
import Box from '@mui/material/Box';
import { moveMapTo } from '../../store/slices/map';

const SignIn = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const auth = useAppSelector((state) => state.auth);
    const sessionStatus = useAppSelector((state) => state.sessionStatus);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [formError, setFormError] = useState('');
    const { userLogingIn, userLoginError, userLoginSuccess, loginError } = auth;
    const { pathBeforeLoginRequest } = sessionStatus;

    useEffect(() => {
        if (!userLogingIn && userLoginSuccess) {
            const data = auth.tokenData?.data;
            if (data?.latitude && data?.longitude) {
                dispatch(
                    moveMapTo({
                        center: {
                            lat: parseFloat(data.latitude),
                            lng: parseFloat(data.longitude),
                        },
                        zoom: parseFloat(data.zoom) || 12,
                    })
                );
            }

            if (data?.terms === false) {
                history.push('/accept-terms');
                return;
            }

            if (pathBeforeLoginRequest) {
                history.push(pathBeforeLoginRequest);
                dispatch(unsetPathBeforeLoginRequest());
            } else {
                history.push('/');
            }
        }
    }, [userLogingIn, userLoginSuccess]);

    useEffect(() => {
        if (!userLogingIn && loginError) {
            dispatch(logout());
        }
        return () => {
            if (userLoginError) {
                dispatch(logout());
            }
        };
    }, []);

    const alertMode = userLogingIn || userLoginError || userLoginSuccess;

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setFormError('');
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
        setFormError('');
    };

    const handleLoginClick = () => {
        if (!email || !password) {
            setFormError('Please enter both email and password.');
        } else {
            void dispatch(login({ email, password }));
        }
    };

    return (
        <CustomDialog
            processing={userLogingIn}
            processingCopy={'Logging in...'}
            error={userLoginError}
            errorContent={
                <ErrorMessage
                    mainCopy={
                        'If you haven\'t yet verified your account, please check your email and do so to enable your account'
                    }
                    specificError={loginError}
                />
            }
            success={userLoginSuccess}
            title={'Sign in'}
            onClose={() => {
                if (userLoginError) {
                    dispatch(logout());
                }
                history.push('/');
            }}
            sx={{
                '& .MuiDialog-container': {
                    width: '600px'
                }
            }}
        >
            <form>
                <Grid container spacing={1}>
                    <Grid xs={12}>
                        <GoogleSignInButton />
                    </Grid>
                    <Grid xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ccc' }} />
                        <Typography variant="caption" color="text.secondary">or sign in with email</Typography>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ccc' }} />
                    </Grid>
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            label='Email'
                            type='email'
                            value={email}
                            onChange={handleEmailChange}
                            placeholder='Enter email'
                            error={!!formError && !email}
                        />
                    </Grid>
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            label='Password'
                            type='password'
                            value={password}
                            onChange={handlePasswordChange}
                            placeholder='Password'
                            error={!!formError && !password}
                        />
                    </Grid>
                    {formError && (
                        <Grid xs={12}>
                            <Box color='error.main' textAlign='center' mt={1}>
                                {formError}
                            </Box>
                        </Grid>
                    )}
                    <Grid xs={12}>
                        <Box sx={{ textAlign: 'right' }}>
                            <Link color='secondary' href='/forgot-password' underline='hover' variant='caption'>
                                Forgot password?
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
                <Grid>
                    <Grid xs={12}>
                        {!alertMode ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    textAlign: 'center',
                                    pt: 2,
                                }}
                            >
                                <Button
                                    variant='contained'
                                    color={'primary'}
                                    onClick={handleLoginClick}
                                    disabled={userLogingIn}
                                >
                                    Log in
                                </Button>
                                {!userLoginSuccess && !userLoginError && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant='body2' color='text.secondary'>
                                            Don&apos;t have an account?{' '}
                                            <Link color='secondary' href='/sign-up' underline='hover'>
                                                Sign up
                                            </Link>
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                    </Grid>
                </Grid>
            </form>
        </CustomDialog>
    );
};

export default SignIn;
